import { PASSIVES, passiveById, passiveMax } from '../data/passives';
import { EVOLUTIONS, evolutionById } from '../data/evolutions';
import { UNIONS, unionById } from '../data/unions';
import { GRANT } from './meta-parts';
import { WEAPON_POOL, weaponById as poolWeaponById } from '../data/weapons-pool';
import { recalcStats, registerModSource } from './stats';
import { recalcRules } from './rules';
import { canTakeMechUp, mechUpDigest, resetMechUps, takeMechUp } from './mech-upgrades';
import { improviseDigest, resetImprovises, takeImprovise } from './improvise';
import { improviseById } from '../data/improvise';
import { mechUpById } from '../data/mech-upgrades';
import { PROTOCOLS, protocolById } from '../data/protocols';
import { canTakeProtocol, protocolDigest, resetProtocols, takeProtocol } from './protocols';
import { resetDraft } from './draft';
import { metCraft } from './metrics';
import { P } from '../world/player';
/* ============================================================
   槽位（M5）
   ============================================================

   **武裝槽 5 / 被動槽 6**，每項最高 5 級（規則類的被動比較短，見 `passiveMax`）。

   槽位的演變（2026-08-02 一天內兩次，兩次都有理由）：

     6 把武裝配 6 格 → 拿得到 100%，「這一場抽到什麼」不存在，每場結局一樣
     先降到 4        → 製造取捨，但那是止痛藥：池子太小才是病根
     池子補到 10 把  → **調回 5**（拿得到 50%），這是當初就寫死的條件

   被動池 22 個配 6 格（27%），稀缺性由池子大小提供，不用再縮格子。

   **機體給的三把（起手主武裝 + 兩個主動技）不佔槽位、也不進池子**，
   維持 `sim/player.ts` 既有的 `P.mech.primary/mid/ult` 路徑。
   理由見 GDD §7.1：池子若等於機體武裝，「這場抽到什麼」的驚喜只剩三次。

   **陣列而不是物件**：抽選與開火都會走訪它，順序必須是決定性的。
   物件的 key 順序在規格上雖然對整數以外的 key 有保證，但依賴那個保證
   來維持對局的決定性太脆弱了。 */

/* 武裝槽 **4 → 5**（2026-08-02 第二批池子）。設計文件當初寫死了條件：
   「池子補到 10 把以上時，這個數字應該調回 5–6」。池子現在正好 10 把，
   所以照約定調回 5 —— 拿得到 50%，仍然是真的取捨。 */
export var MAX_W = 5;          // 武裝槽（見檔頭）
export var MAX_P = 6;          // 被動槽：那一側的稀缺性由「池子從 8 擴到 16」提供
export var MAX_LEVEL = 5;
/** 相容用的舊名。新程式碼請用 MAX_W / MAX_P。 */
export var MAX_SLOT = MAX_P;

/** 質變（進化／融合）的等級上限。**3，而且跟 `MAX_LEVEL` 是兩個常數。**

    2026-08-05 之前質變只有一級（「拿到就是完全體」）。五次配平實驗之後
    那條規則被證明是後期決策真空的**結構原因**：18 條質變全部拿到
    ＝ 5 個武裝槽全是死格，池子因此更早見底
    （`.claude/notes/enjoy-gap-2026-08-05.md` 的實驗 E）。

    3 而不是 5：質變是 build 的頂點，級數太長會讓「拿到質變」那一下
    被稀釋成又一條要練滿的長條；而且質變 Lv1 已經是來源的 2.5–4 倍
    （`04-build-system` 的帶寬），再乘五級會直接壓過難度曲線。

    **Lv1 的行為與數值刻意跟改動前逐位元相同** —— 量測台量到的倍率、
    黃金雜湊裡的第一分鐘都不該因為「加了等級」而變。成長寫在 Lv2/Lv3。 */
export var CRAFT_MAX = 3;

/** 質變的**取得門檻**：來源武裝要幾級（`CRAFT_GATE_W`）、鑰匙被動要幾級
    （`CRAFT_GATE_P`，實際門檻是它與那一項自己的上限取小，見 `craftGateP`）。

    **跟 `MAX_LEVEL` 是兩個常數。** 2026-08-06 之前這兩件事共用 `MAX_LEVEL`，
    於是「等級上限」與「質變門檻」被綁死，動不了其中一個。
    分開之後門檻可以獨立調（實驗 E：5+滿級 → 3+3），
    而「一項最多練幾級」仍然是 `MAX_LEVEL`。

    改這兩個數字時，**三個計算點必須一起看**（歷史上漏改過兩次，都是測試抓到的）：
    `evoReady`／`unionReady`（本檔）、`sim/draft.ts` 的 `craftCompletedBy()`
    （進化推力 ×4.0 的「差最後一級」判定）、`probe-slots.ts` 的
    `evoState()`／`uniState()`（湊料進度）。UI 那一側是 `ui/screens/pause.ts`。 */
export var CRAFT_GATE_W = 5;
export var CRAFT_GATE_P = 5;

/** 鑰匙被動的實際門檻：那一項自己的上限與 `CRAFT_GATE_P` 取小的。
    規則類被動只有 3 級，門檻寫死 5 的話拿它當鑰匙的進化會永遠湊不到，
    而且不會有任何錯誤，只是那個進化從此不存在。 */
export function craftGateP(key) { return Math.min(passiveMax(key), CRAFT_GATE_P); }

export type Owned = {
  id: string, lv: number, cd: number,
  /** 持續型武裝的私有狀態。放在槽位上而不是模組變數 ——
      模組變數會讓下一場開局帶著上一場的殘留。 */
  st?: any
};

export var weapons: Owned[] = [];
export var passives: Owned[] = [];

export function resetLoadout() {
  for (var i = 0; i < weapons.length; i++) {
    var d = weaponById(weapons[i].id);
    if (d && d.onRemove) d.onRemove(weapons[i]);   // 收掉視覺物件，不然會留在場上
  }
  weapons.length = 0;
  passives.length = 0;
  /* 機體武裝強化（Boss 獎勵）同樣是「這一場的持有狀態」，
     跟著這裡一起清 —— 分開清的話下一場會帶著上一場打贏的 Boss 獎勵。 */
  resetMechUps();
  /* 應急改裝（可選池見底之後的無限強化）同樣是「這一場的持有狀態」。
     併進這一個入口而不是各自在 startRun 加一行 —— 理由同上面那幾條。 */
  resetImprovises();
  /* 破格協定與抽卡狀態（reroll／banish／保底計數）同理。**併進這一個入口**
     而不是各自在 `startRun` 加一行：這個專案記過「新欄位忘了在 startRun 歸零」，
     而測試直接呼叫的也是 `resetLoadout()` —— 分開清的話測試漏得最快。 */
  resetProtocols();
  resetDraft();
  recalcStats();
  recalcRules();                 // **一定要跟著清**，否則上一場的規則會留到下一場
}

/** 武裝查表：池子與進化型都算。 */
export function weaponById(id) { return poolWeaponById(id) || evolutionById(id) || unionById(id); }

/** 進化條件成立嗎：來源武裝與鑰匙被動都到門檻（見 `CRAFT_GATE_W`／`craftGateP`）。 */
export function evoReady(evo) {
  /* 鑰匙被動的門檻走 `craftGateP` —— 它已經把「那一項自己的上限」夾進去了，
     不要在這裡直接比 `CRAFT_GATE_P`（規則類被動只有 3 級，會永遠湊不到）。 */
  var key = passiveById(evo.evo.needs);
  return weaponLevel(evo.evo.from) >= CRAFT_GATE_W && passiveLevel(evo.evo.needs) >= craftGateP(key);
}

/** 融合條件成立嗎：**兩把武裝都到門檻**。被動無關。 */
export function unionReady(u) {
  return weaponLevel(u.union.a) >= CRAFT_GATE_W && weaponLevel(u.union.b) >= CRAFT_GATE_W;
}

/** 進化或融合（統稱「合成品」）。這兩種只能靠寶箱拿到，見 `ui/levelup.ts`。 */
export function craftById(id) { return evolutionById(id) || unionById(id); }

/* 槽位上限吃局外部件（掛架）。**用函式不用常數** ——
   `MAX_SLOT` 是「基準線」，實際上限每一場都可能不同。
   直接改常數的話測試與 UI 會各自看到不同的數字。 */
export function weaponCap() { return MAX_W + GRANT.wSlot; }
export function passiveCap() { return MAX_P + GRANT.pSlot; }

function find(list: Owned[], id) {
  for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
  return null;
}
export function weaponLevel(id) { var o = find(weapons, id); return o ? o.lv : 0; }
export function passiveLevel(id) { var o = find(passives, id); return o ? o.lv : 0; }
export function levelOf(id) { return weaponLevel(id) || passiveLevel(id); }

/** 這一項現在能不能被選：沒滿級，而且（如果是新的）還有空槽。 */
export function canTake(id) {
  /* 機體武裝強化走自己的規則：**不佔槽、只看自己的上限**。
     擺在最前面是因為它既不是池子武裝也不是被動，下面每一條分支都不認得它。 */
  if (mechUpById(id)) return canTakeMechUp(id);
  /* 應急改裝：不佔槽、**沒有上限**（`data/improvise.ts`）。
     擺在最前面的理由跟機體武裝強化一樣 —— 它不在任何一個池子裡，
     漏了這一條的話下面每個分支都不認得它，`canTake` 會靜靜回 false。 */
  if (improviseById(id)) return true;
  /* 破格協定同樣不佔槽、只有一級、一場最多兩個（`data/protocols.ts`）。
     **一定要排在被動分支之前**：它不在 `PASSIVES` 裡，
     漏了這一條的話下面每個分支都不認得它，`canTake` 會靜靜回 false。 */
  if (protocolById(id)) return canTakeProtocol(id);
  var ev = evolutionById(id);
  if (ev) {
    /* 進化是**取代**來源武裝，所以不看槽位有沒有空。
       已經進化過的就走「練深」那條路（上限 `CRAFT_MAX`）——
       這一條是 2026-08-05「每個質變都是死路」的解法本體。 */
    var elv = weaponLevel(id);
    if (elv > 0) return elv < CRAFT_MAX;
    return evoReady(ev);
  }
  var un = unionById(id);
  if (un) {
    /* 融合吃掉兩把、放回一把，所以槽位只會變鬆，同樣不用看有沒有空格。 */
    var ulv = weaponLevel(id);
    if (ulv > 0) return ulv < CRAFT_MAX;
    return unionReady(un);
  }
  var w = weaponById(id);
  if (w) {
    var lv = weaponLevel(id);
    if (lv > 0) return lv < MAX_LEVEL;
    return weapons.length < weaponCap();
  }
  var p = passiveById(id);
  if (p) {
    var lv2 = passiveLevel(id);
    /* **每一項有自己的上限**（`passiveMax`）：規則類的離散效果刻意比較短，
       例如「緊急重啟模組」只有 3 級 —— 復活次數是二元的事實不是連續加成，
       給到 5 級會讓它變成唯一正解。 */
    if (lv2 > 0) return lv2 < passiveMax(p);
    return passives.length < passiveCap();
  }
  return false;
}

/** 一級空轉能換到多少結構上限。小到不會變成「刻意擺爛等補給」的誘因，
    大到玩家看得出來自己拿到了東西。 */
export var SUPPLY_HP = 6;

/** 池子見底時的替代品：把 `n` 級空轉換成結構上限。回傳實際加了多少。

    這條路徑存在的理由見 `ui/levelup.ts` 的呼叫處 —— 原本是把整個升級佇列
    靜默歸零，玩家升了級卻什麼都沒有、也沒有任何訊息。
    `03-in-run-progression.md` §一：候選不足時用回復或小額資源填充，
    **永遠不要讓玩家空手**。

    放在 sim 層而不是 UI 層：它改的是對局狀態（會進狀態快照與黃金雜湊），
    UI 只負責顯示。 */
export function takeSupply(n: number) {
  if (!(n > 0)) return 0;
  var gain = SUPPLY_HP * n;
  P.hpMax += gain;
  P.hp = Math.min(P.hpMax, P.hp + gain);
  return gain;
}

/** 取得或升一級。回傳升到第幾級（0 = 沒成功）。 */
export function take(id) {
  if (!canTake(id)) return 0;
  /* 機體武裝強化：不進 weapons／passives 任何一個清單（它不佔槽），
     所以在這裡就分流出去。走同一個 `take()` 的理由是入口只留一個 ——
     升級介面、除錯主控台、測試的 `api.grant` 因此都不必各自認得它。 */
  if (mechUpById(id)) return takeMechUp(id);
  if (improviseById(id)) return takeImprovise(id);
  if (protocolById(id)) return takeProtocol(id);
  /* 已經持有的質變＝升一級。**擺在取代／融合那兩段之前** ——
     否則第二次拿到同一個進化時會再跑一次「找來源武裝」，
     而來源早就不在陣列裡了，`take()` 會靜靜回 0。 */
  if (craftById(id)) {
    var cur = find(weapons, id);
    if (cur) { cur.lv++; return cur.lv; }
  }
  var ev = evolutionById(id);
  if (ev) {
    /* 進化取代來源武裝：先把來源收掉（含它的視覺物件），再把進化型放進同一個位置。
       用 splice + 插回原索引而不是 push —— 順序穩定，決定性才不會受影響。 */
    for (var r = 0; r < weapons.length; r++) {
      if (weapons[r].id !== ev.evo.from) continue;
      var src = weaponById(ev.evo.from);
      if (src && src.onRemove) src.onRemove(weapons[r]);
      var slot: Owned = { id: id, lv: 1, cd: 0, st: null };
      weapons.splice(r, 1, slot);
      if (ev.onEquip) ev.onEquip(slot);
      metCraft(id, [ev.evo.from]);
      return 1;
    }
    return 0;
  }
  var un2 = unionById(id);
  if (un2) {
    /* 融合：**兩把都移除，新的放在第一把的位置** —— 槽位淨減一格。
       這是融合與進化的關鍵差別（VS wiki: "hence emptying a weapon slot"），
       也是玩家願意把兩把都練滿的理由：練滿的代價會退回來。 */
    var ia = -1, ib = -1, r2;
    for (r2 = 0; r2 < weapons.length; r2++) {
      if (weapons[r2].id === un2.union.a) ia = r2;
      if (weapons[r2].id === un2.union.b) ib = r2;
    }
    if (ia < 0 || ib < 0) return 0;
    var wa = weaponById(un2.union.a), wb = weaponById(un2.union.b);
    if (wa && wa.onRemove) wa.onRemove(weapons[ia]);
    if (wb && wb.onRemove) wb.onRemove(weapons[ib]);
    var keep = Math.min(ia, ib), drop = Math.max(ia, ib);
    weapons.splice(drop, 1);                    // 先刪大的索引，小的才不會位移
    var uslot: Owned = { id: id, lv: 1, cd: 0, st: null };
    weapons.splice(keep, 1, uslot);
    if (un2.onEquip) un2.onEquip(uslot);
    metCraft(id, [un2.union.a, un2.union.b]);
    return 1;
  }
  var isW = !!weaponById(id);
  var list = isW ? weapons : passives;
  var o = find(list, id);
  if (o) { o.lv++; }
  else {
    o = { id: id, lv: 1, cd: 0, st: null };
    list.push(o);
    if (isW) { var wd = weaponById(id); if (wd && wd.onEquip) wd.onEquip(o); }
  }
  if (!isW) {
    recalcStats();               // 被動改變 → 重算修正層
    recalcRules();               // 規則類效果也要重算（sim/rules.ts）
    var def = passiveById(id);
    if (def && def.onTake) def.onTake(o.lv);   // 不在 P.s 裡的效果走增量路徑
  }
  return o.lv;
}

/** 條件成立的合成品（進化 + 融合）。**只有開寶箱時才會出現在選項裡**
    —— 照 VS 的規則（`Evolution` wiki 頁：進化品是寶箱的第一個獎勵）。
    這讓寶箱從「多三次升級」變成「build 的轉捩點」，也給了精英一個被追殺的理由。 */
export function craftIds() {
  var out: string[] = [], i;
  /* **只有還沒拿到的**。質變的第 2、3 級走升級選單（`craftLevelIds()`），
     不從寶箱出 —— 寶箱給的是「build 的轉捩點」那一下，
     而且寶箱一場吃掉 50 張、升級選單只有 14 張，
     兩邊共用同一份供給的話新加的等級會有七成被寶箱吸走
     （`.claude/notes/enjoy-gap-2026-08-05.md` 結論二）。 */
  for (i = 0; i < EVOLUTIONS.length; i++) {
    if (weaponLevel(EVOLUTIONS[i].id) === 0 && canTake(EVOLUTIONS[i].id)) out.push(EVOLUTIONS[i].id);
  }
  for (i = 0; i < UNIONS.length; i++) {
    if (weaponLevel(UNIONS[i].id) === 0 && canTake(UNIONS[i].id)) out.push(UNIONS[i].id);
  }
  return out;
}

/** 已經持有、還沒練滿的質變。**升級選單專屬的供給**（見 `craftIds()` 的註解）。

    走 `weapons` 而不是走 `EVOLUTIONS`／`UNIONS`：順序＝槽位順序，
    決定性因此跟著槽位走，不會因為資料檔重新排序而改變同一顆種子的結果。 */
export function craftLevelIds() {
  var out: string[] = [];
  for (var i = 0; i < weapons.length; i++) {
    if (craftById(weapons[i].id) && weapons[i].lv < CRAFT_MAX) out.push(weapons[i].id);
  }
  return out;
}

/** 一般升級可以抽到的項目（**不含**進化與融合）。 */
export function availableIds() {
  var out: string[] = [], i;
  for (i = 0; i < WEAPON_POOL.length; i++) if (canTake(WEAPON_POOL[i].id)) out.push(WEAPON_POOL[i].id);
  for (i = 0; i < PASSIVES.length; i++) if (canTake(PASSIVES[i].id)) out.push(PASSIVES[i].id);
  return out;
}

/** 抽卡的候選池 ＝ 一般升級 ＋ 破格協定。

    **協定只加在這裡，不加進 `availableIds()`。** 寶箱（`sim/chest.ts`）用的是
    `availableIds()` 而且是均勻隨機抽 —— 協定要是進了那個池子，
    它在寶箱裡的機率會是「6 ÷ 池子大小」≈ 20%，而稀有度表給傳說的是 1–10%。
    那張表會被整個繞過去，而且是靜默的（開出來的東西看起來完全正常）。

    協定不佔槽，所以 12 分鐘後六格滿了仍然抽得到 ——
    那正是 03 要求「12 分鐘後 25% 出現非線性選項」的時間點。 */
export function draftPoolIds(noCraftLv?: boolean) {
  var out = availableIds();
  for (var i = 0; i < PROTOCOLS.length; i++) if (canTake(PROTOCOLS[i].id)) out.push(PROTOCOLS[i].id);
  /* 質變的第 2、3 級。`noCraftLv` 是給寶箱用的 —— 見 `craftIds()` 的註解。 */
  if (!noCraftLv) out = out.concat(craftLevelIds());
  return out;
}

/** 給快照用：把持有狀態壓成一個短字串，否則 build 對黃金雜湊是隱形的。 */
export function loadoutDigest() {
  var s = '';
  for (var i = 0; i < weapons.length; i++) s += weapons[i].id + weapons[i].lv + ',';
  s += '|';
  for (var j = 0; j < passives.length; j++) s += passives[j].id + passives[j].lv + ',';
  /* 機體武裝強化**只在真的拿過時才寫進去**。無條件加一個分隔字元的話，
     每一場的摘要字串都會變 —— 黃金雜湊吃這個字串，於是一個純表示法的改動
     會偽裝成「模擬結果變了」，把真正的行為差異埋掉。 */
  var m = mechUpDigest() + improviseDigest();
  if (m) s += '|' + m;
  var pr = protocolDigest();
  if (pr) s += '|' + pr;
  return s;
}

/* 被動的修正丟進數值層。註冊一次就好 —— 之後 take() 只要呼叫 recalcStats。 */
registerModSource(function () {
  var out = [];
  for (var i = 0; i < passives.length; i++) {
    var def = passiveById(passives[i].id);
    if (def) out.push(def.mods(passives[i].lv));
  }
  return out;
});
